import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IFilter } from 'src/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItem';
import { IFilterCompetence } from 'src/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItemCompetence';
import { getApiDateFormat } from 'src/utils/date/date';
import { postFetch } from 'src/utils/fetcher';
import { POST_EMPLOYEE_SEARCH_KEY } from './postEmployeeSearchAPI.constants';
import {
  TEmployeeSearchReq,
  TEmployeeSearchReqCompetence,
  TEmployeeSearchReqDepartment,
  TEmployeeSearchReqLegalEntity,
  TEmployeeSearchReqManager,
  TEmployeeSearchRes,
} from './postEmployeeSearchAPI.types';
import { ASSIGN_TAKE_ITEMS } from '../../constants';

const POST_EMPLOYEE_SEARCH_URL_PATH = '/api/v2/resource-planner/employee-search';

const postEmployeeSearch = (reqBody: TEmployeeSearchReq): Promise<TEmployeeSearchRes> =>
  postFetch({
    path: POST_EMPLOYEE_SEARCH_URL_PATH,
    key: POST_EMPLOYEE_SEARCH_KEY,
    body: JSON.stringify(reqBody),
  });

interface IUsePostEmployeeSearch {
  taskId: string;
  competence?: IFilterCompetence;
  department?: IFilter;
  endDate?: Date | null;
  hours?: number;
  legalEntity?: IFilter;
  manager?: IFilter;
  query?: string;
  startDate?: Date | null;
  take?: number;
  skip?: string;
}

export const usePostEmployeeSearch = ({
  competence,
  department,
  endDate,
  hours,
  legalEntity,
  manager,
  query,
  startDate,
  taskId,
  take = ASSIGN_TAKE_ITEMS,
  skip,
}: IUsePostEmployeeSearch) => {
  const Manager: TEmployeeSearchReqManager = manager?.values?.length
    ? {
        ManagerIds: manager.values.map((m) => m.value).toString(),
        IsManagerInclusive: manager.isInclude,
      }
    : {
        ManagerIds: undefined,
        IsManagerInclusive: undefined,
      };

  const Department: TEmployeeSearchReqDepartment = department?.values?.length
    ? {
        DepartmentIds: department.values.map((d) => d.value).toString(),
        IsDepartmentInclusive: department.isInclude,
      }
    : {
        DepartmentIds: undefined,
        IsDepartmentInclusive: undefined,
      };

  const LegalEntity: TEmployeeSearchReqLegalEntity = legalEntity?.values?.length
    ? {
        LegalEntityIds: legalEntity.values.map((l) => l.value).toString(),
        IsLegalEntityInclusive: legalEntity.isInclude,
      }
    : {
        LegalEntityIds: undefined,
        IsLegalEntityInclusive: undefined,
      };

  const Competence: TEmployeeSearchReqCompetence = competence?.values?.length
    ? {
        Competences: competence.values.map((c) => ({ Id: c.value, Value: c.levels.toString() })),
        IsCompetenceInclusive: true,
      }
    : {
        Competences: undefined,
        IsCompetenceInclusive: undefined,
      };

  const reqBody: TEmployeeSearchReq = {
    Hours: hours,
    PeriodEndDate: getApiDateFormat(endDate ?? new Date()),
    PeriodStartDate: getApiDateFormat(startDate ?? new Date()),
    SearchText: query?.trim(),
    TaskId: Number(taskId),
    Take: take,
    Skip: skip,
    ...Competence,
    ...Department,
    ...LegalEntity,
    ...Manager,
  };

  const filteredReqBody = Object.keys(reqBody).reduce((a, b) => {
    const key = b as keyof TEmployeeSearchReq;
    if (reqBody[key] !== undefined && reqBody[key] !== null && reqBody[key] !== '') {
      return { ...a, [key]: reqBody[key] };
    }
    return a;
  }, {} as TEmployeeSearchReq);

  const {
    data: tempDate,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    ...queryResult
  } = useInfiniteQuery<TEmployeeSearchRes>({
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 || (lastPage.items?.length ?? 0) < take
        ? null
        : lastPage.nextSkip,
    queryKey: [POST_EMPLOYEE_SEARCH_KEY, filteredReqBody],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await postEmployeeSearch({
        Skip: String((filteredReqBody?.Take ?? 0) * pageParam),
        ...filteredReqBody,
      });

      return {
        items: response.entities ?? [], // Assuming response is an array of items
        nextSkip:
          (response.entities?.length ?? 0) < (filteredReqBody?.Take ?? 0) ? null : pageParam + 1,
      };
    },
  });

  const observerRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!observerRef.current || !hasNextPage || isFetchingNextPage) return () => {};

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const data = useMemo(
    () => tempDate?.pages?.flatMap((page) => page.items) ?? [],
    [tempDate?.pages],
  );

  return { data, fetchNextPage, hasNextPage, isFetchingNextPage, observerRef, ...queryResult };
};
